import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  VStack,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import rolePermissionService from 'services/rolePermissionService';

export default function UserFormModal({ isOpen, onClose, user, roles, isEditing, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    user: '',
    email: '',
    password: '',
    confirmPassword: '',
    roleId: '',
    enabled: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      if (isEditing && user) {
        setFormData({
          name: user.name || '',
          user: user.user || '',
          email: user.email || '',
          password: '',
          confirmPassword: '',
          roleId: user.role?.id || '',
          enabled: user.enabled !== undefined ? user.enabled : true,
        });
      } else {
        setFormData({
          name: '',
          user: '',
          email: '',
          password: '',
          confirmPassword: '',
          roleId: '',
          enabled: true,
        });
      }
    }
  }, [isOpen, isEditing, user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Error',
        description: 'El nombre es requerido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!formData.user.trim()) {
      toast({
        title: 'Error',
        description: 'El usuario es requerido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // Validar email solo si se proporciona
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast({
          title: 'Error',
          description: 'Ingresa un email válido',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
    }

    // Validar contraseña solo si no es edición o si se está cambiando
    if (!isEditing || formData.password) {
      if (!formData.password) {
        toast({
          title: 'Error',
          description: 'La contraseña es requerida',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }

      if (formData.password.length < 6) {
        toast({
          title: 'Error',
          description: 'La contraseña debe tener al menos 6 caracteres',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        toast({
          title: 'Error',
          description: 'Las contraseñas no coinciden',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const userData = {
        name: formData.name,
        user: formData.user,
        enabled: formData.enabled,
      };

      // Incluir email solo si se proporciona
      if (formData.email && formData.email.trim()) {
        userData.email = formData.email;
      }

      // Solo incluir contraseña si no es edición o si se está cambiando
      if (!isEditing || formData.password) {
        userData.password = formData.password;
      }

      if (isEditing) {
        await rolePermissionService.updateUser(user.id, userData);
        
        // Asignar rol si se especificó
        if (formData.roleId) {
          await rolePermissionService.assignRoleToUser(user.id, parseInt(formData.roleId));
        }
      } else {
        const response = await rolePermissionService.createUser(userData);
        
        // Asignar rol si se especificó
        if (formData.roleId && response.payload?.user?.id) {
          await rolePermissionService.assignRoleToUser(
            response.payload.user.id, 
            parseInt(formData.roleId)
          );
        }
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.message || `Error al ${isEditing ? 'actualizar' : 'crear'} el usuario`;
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      user: '',
      email: '',
      password: '',
      confirmPassword: '',
      roleId: '',
      enabled: true,
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditing ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Nombre Completo</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Ej: Juan Pérez"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Usuario</FormLabel>
              <Input
                type="text"
                value={formData.user}
                onChange={(e) => handleInputChange('user', e.target.value)}
                placeholder="usuario123"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email (opcional)</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="usuario@ejemplo.com"
              />
            </FormControl>

            <FormControl isRequired={!isEditing}>
              <FormLabel>
                Contraseña 
                {isEditing && <Text as="span" fontSize="sm" color="gray.500">(dejar vacío para mantener actual)</Text>}
              </FormLabel>
              <InputGroup>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                />
                <InputRightElement>
                  <IconButton
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                    variant="ghost"
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {(!isEditing || formData.password) && (
              <Text fontSize="sm" color="gray.600">
                Al ingresar con esta contraseña, la persona deberá cambiarla.
              </Text>
            )}

            {(!isEditing || formData.password) && (
              <FormControl isRequired>
                <FormLabel>Confirmar Contraseña</FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    placeholder="Confirma la contraseña"
                  />
                  <InputRightElement>
                    <IconButton
                      h="1.75rem"
                      size="sm"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      icon={showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            )}

            <FormControl>
              <FormLabel>Rol</FormLabel>
              <Select
                value={formData.roleId}
                onChange={(e) => handleInputChange('roleId', e.target.value)}
                placeholder="Seleccionar rol (opcional)"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name} - {role.description}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="user-active" mb="0">
                Usuario Activo
              </FormLabel>
              <Switch
                id="user-active"
                isChecked={formData.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="gray"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText={isEditing ? 'Actualizando...' : 'Creando...'}
          >
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}